import i18n from 'i18n';
// Models
import IAuthCredential from 'app/models/AuthCredential';
import ILabel from 'app/models/Label';

const STORAGE_KEY = 'cases-portal';

enum StorageUserPreferences {
  CasesSorting = 'casesSorting',
  CollectionTab = 'collectionTab',
  DocumentsPreferences = 'documentsPreferences',
  WorkflowsPreferences = 'workflowsPreferences',
  InvoicesPreferences = 'invoicesPreferences'
};

export default class LocalStorageService {
  private static readonly ACCESS_TOKEN:string = `${STORAGE_KEY}:access_token`;
  private static readonly ACCESS_TOKEN_TYPE:string = `${STORAGE_KEY}:access_token_type`;
  private static readonly EXPIRES_ON_TIME:string = `${STORAGE_KEY}:expires_on_time`;
  private static readonly REFRESH_TOKEN:string = `${STORAGE_KEY}:refresh_token`;

  private static readonly FILES_SIDEBAR_OPEN:string = `${STORAGE_KEY}:files_sidebar_open`;
  private static readonly WORKSPACE_SIDEBAR_OPEN:string = `${STORAGE_KEY}:workspace_sidebar_open`;

  private static readonly PAGE_EPISODES_SORTING:string = `${STORAGE_KEY}:page_episodes_sorting`;

  public static getAccessToken():string {
    return localStorage.getItem(this.ACCESS_TOKEN) || '';
  };
  public static getRefreshToken():string {
    return localStorage.getItem(this.REFRESH_TOKEN) || '';
  };
  public static getExpiresOnTime():number {
    const expiresOn = localStorage.getItem(this.EXPIRES_ON_TIME) || '0';
    return parseInt(expiresOn, 10);
  };
  public static getAccessTokenType():string {
    return localStorage.getItem(this.ACCESS_TOKEN_TYPE) || '';
  };

  public static isAccessTokenExpired():boolean {
    if ( !this.getAccessToken() ) return true;
    const currentTime = new Date().getTime();
    const expiresOnTime = this.getExpiresOnTime();
    return currentTime > expiresOnTime;
  };

  public static setAuthCredential(authCredential:IAuthCredential):void {
    const { accessToken, refreshToken, expiresIn, tokenType } = authCredential;
    const currentDate = new Date();
    localStorage.setItem(this.ACCESS_TOKEN, accessToken);
    if ( refreshToken ) localStorage.setItem(this.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(this.EXPIRES_ON_TIME, currentDate.setSeconds(
      currentDate.getSeconds() + expiresIn
    ).toString());
    localStorage.setItem(this.ACCESS_TOKEN_TYPE, tokenType);
  };

  public static clearAuthCredential():void {
    localStorage.removeItem(this.ACCESS_TOKEN);
    localStorage.removeItem(this.ACCESS_TOKEN_TYPE);
    localStorage.removeItem(this.EXPIRES_ON_TIME);
    localStorage.removeItem(this.REFRESH_TOKEN);
  };

  public static getBatchesSidebarOpen = (type: 'files' | 'workspace'):boolean => {
    const key = type === 'files' ? this.FILES_SIDEBAR_OPEN : this.WORKSPACE_SIDEBAR_OPEN;
    const batchesSidebarOpen = localStorage.getItem(key) || 'true';
    return batchesSidebarOpen === 'true' ? true : false;
  }
  public static setBatchesSidebarOpen = (type: 'files' | 'workspace', open:boolean) => {
    const key = type === 'files' ? this.FILES_SIDEBAR_OPEN : this.WORKSPACE_SIDEBAR_OPEN;
    localStorage.setItem(key, JSON.stringify(open));
  }

  // UI
  private static readonly USER_PREFERENCES:string = `${STORAGE_KEY}:user_preferences`;

  private static getUserPreferences = (key?:StorageUserPreferences) => {
    const userPreferences = localStorage.getItem(this.USER_PREFERENCES) || '{}';
    const parsedUserPreferences = JSON.parse(userPreferences);
    return key ? parsedUserPreferences[key] : parsedUserPreferences;
  }

  private static setUserPreferences = (key:StorageUserPreferences, data:any) => {
    const userPreferences = this.getUserPreferences();
    userPreferences[key] = data;
    localStorage.setItem(this.USER_PREFERENCES, JSON.stringify(userPreferences));
  }

  // Case Sorting
  public static getCasesSorting = (accountId:number) => {
    const casesSorting = this.getUserPreferences(StorageUserPreferences.CasesSorting);
    return casesSorting && casesSorting[accountId] ? casesSorting[accountId] : {};
  }

  public static setCasesSorting = (accountId:number, data:any) => {
    const casesSorting = this.getUserPreferences(StorageUserPreferences.CasesSorting);
    this.setUserPreferences(StorageUserPreferences.CasesSorting, {
      ...casesSorting,
      [accountId]: data
    });
  }

  // Collection Tab
  public static getCollectionTab = (accountId:number):number => {
    const collectionTab = this.getUserPreferences(StorageUserPreferences.CollectionTab);
    return collectionTab && collectionTab[accountId] ? collectionTab[accountId] : 0;
  }

  public static setCollectionTab = (accountId:number, tab:number) => {
    const collectionTab = this.getUserPreferences(StorageUserPreferences.CollectionTab);
    this.setUserPreferences(StorageUserPreferences.CollectionTab, {
      ...collectionTab,
      [accountId]: tab
    });
  }

  // Documents Preferences
  public static getDocumentsPreferences = (field:string) => {
    const documentsPreferences = this.getUserPreferences(StorageUserPreferences.DocumentsPreferences) || {};
    return documentsPreferences[field];
  }

  public static setDocumentsPreferences = (field:string, value:any) => {
    this.setUserPreferences(StorageUserPreferences.DocumentsPreferences, {
      ...(this.getUserPreferences(StorageUserPreferences.DocumentsPreferences) || {}),
      [field]: value
    });
  }

  // Workflows Preferences
  public static getWorkflowsPreferences = (field:string) => {
    const workflowsPreferences = this.getUserPreferences(StorageUserPreferences.WorkflowsPreferences) || {};
    return workflowsPreferences[field];
  }

  public static setWorkflowsPreferences = (field:string, value:any) => {
    this.setUserPreferences(StorageUserPreferences.WorkflowsPreferences, {
      ...(this.getUserPreferences(StorageUserPreferences.WorkflowsPreferences) || {}),
      [field]: value
    });
  }

  // Invoices Preferences
  public static getInvoicesPreferences = (field:string) => {
    const invoicesPreferences = this.getUserPreferences(StorageUserPreferences.InvoicesPreferences) || {};
    return invoicesPreferences[field];
  }

  public static setInvoicesPreferences = (field:string, value:any) => {
    this.setUserPreferences(StorageUserPreferences.InvoicesPreferences, {
      ...(this.getUserPreferences(StorageUserPreferences.InvoicesPreferences) || {}),
      [field]: value
    });
  }

  // Page episodes sorting
  public static getPageEpisodesSorting = () => {
    return localStorage.getItem(this.PAGE_EPISODES_SORTING) || 'date';
  }

  public static setPageEpisodesSorting = (sortBy:'date' | 'page') => {
    localStorage.setItem(this.PAGE_EPISODES_SORTING, sortBy);
  }

  // UI
  private static readonly TRIAL_MODE:string = `${STORAGE_KEY}:trial_mode`;
  private static readonly WORKSPACE_PAGE_IDS:string = `${STORAGE_KEY}:workspace_page_ids`;
  private static readonly CALENDAR_DATES:string = `${STORAGE_KEY}:calendar_dates`;
  private static readonly GRID_RESIZER_WIDTH:string = `${STORAGE_KEY}:grid_resizer_width`;
  private static readonly LANGUAGE:string = `${STORAGE_KEY}:user_language`;
  private static readonly SCHEDULE_SEARCH_CARRIER_NUMBER:string = `${STORAGE_KEY}:schedule_search_carrier_number`;
  private static readonly NEW_DESIGN_ENABLED:string = `${STORAGE_KEY}:new_design_enabled`;

  public static setTrialMode = (trialMode:boolean) => {
    localStorage.setItem(this.TRIAL_MODE, JSON.stringify(trialMode));
  }
  public static getTrialMode = () => {
    const trialMode = localStorage.getItem(this.TRIAL_MODE) || 'false';
    return trialMode === 'true' ? true : false;
  }

  public static setWorkspacePageIds = (data:Record<number, string>) => {
    localStorage.setItem(this.WORKSPACE_PAGE_IDS, JSON.stringify(data));
  }
  public static getWorkspacePageIds = () => {
    const workspacePages = localStorage.getItem(this.WORKSPACE_PAGE_IDS) || '{}';
    return JSON.parse(workspacePages);
  }

  public static setCalendarDates = (data:Record<number, string>) => {
    localStorage.setItem(this.CALENDAR_DATES, JSON.stringify(data));
  }
  public static getCalendarDates = () => {
    const calendarDates = localStorage.getItem(this.CALENDAR_DATES) || '{}';
    return JSON.parse(calendarDates);
  }

  public static setNewDesignEnabled(value: boolean): void {
    localStorage.setItem(this.NEW_DESIGN_ENABLED, JSON.stringify(value));
  }

  public static getNewDesignEnabled(): boolean {
    const value = localStorage.getItem(this.NEW_DESIGN_ENABLED);
    return value ? JSON.parse(value) : false;
  }

  // Grid resizer max width
  public static setGridResizerWidth(width:number):void {
    localStorage.setItem(this.GRID_RESIZER_WIDTH, width.toString());
  }
  public static getGridResizerWidth():number {
    const width = window.innerWidth < 1280
      ? '50'
      : window.innerWidth < 1440
        ? '55'
        : window.innerWidth < 1600
          ? '60'
          : window.innerWidth < 1920
            ? '65'
            : '70'
    ;
    const gridResizerWidth = localStorage.getItem(this.GRID_RESIZER_WIDTH) || width;
    return Number(gridResizerWidth);
  }

  // Language
  public static hasLanguage = ():boolean => {
    return Boolean(localStorage.getItem(this.LANGUAGE));
  }

  public static getLanguage = () => {
    return localStorage.getItem(this.LANGUAGE) || 'en';
  }
  public static setLanguage(language:string):void {
    i18n.changeLanguage(language);
    localStorage.setItem(this.LANGUAGE, language);
  }

  public static setScheduleSearchCarrierNumber = (carrierNumber:string) => {
    localStorage.setItem(this.SCHEDULE_SEARCH_CARRIER_NUMBER, carrierNumber);
  }
  public static getScheduleSearchCarrierNumber = ():string => {
    return localStorage.getItem(this.SCHEDULE_SEARCH_CARRIER_NUMBER) || '';
  }

  // Documents labels
  private static readonly DOCUMENTS_LABELS:string = `${STORAGE_KEY}:documents_labels`;

  public static setDocumentsLabels = (labels: ILabel[]) => {
    const existingLabels = this.getDocumentsLabels();
  
    const mergedMap = new Map<string, ILabel>();
  
    const generateKey = (label: ILabel) => `${label.name}_${label.color}`;
  
    [...labels, ...existingLabels].forEach(label => {
      mergedMap.set(generateKey(label), label);
    });
  
    const nextData = Array.from(mergedMap.values()).slice(0, 10);
  
    localStorage.setItem(this.DOCUMENTS_LABELS, JSON.stringify(nextData));
  };

  public static getDocumentsLabels = (): ILabel[] => {
    const documentsLabels = localStorage.getItem(this.DOCUMENTS_LABELS) || "[]";
    return JSON.parse(documentsLabels);
  };

  // Search keywords
  private static readonly SEARCH_KEYWORDS:string = `${STORAGE_KEY}:search_keywords`;

  public static setSearchKeywords = (keyword: string) => {
    const keywords = this.getSearchKeywords();
  
    const filteredKeywords = keywords.filter(k => k !== keyword);
  
    filteredKeywords.unshift(keyword);
  
    const nextData = filteredKeywords.slice(0, 20);
  
    localStorage.setItem(this.SEARCH_KEYWORDS, JSON.stringify(nextData));
  };

  public static getSearchKeywords = (): string[] => {
    const keywords = localStorage.getItem(this.SEARCH_KEYWORDS) || "[]";
    return JSON.parse(keywords);
  };
}
